import type { ReactNode } from 'react';

import { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { toast } from 'src/components/snackbar';

interface QueryProviderProps {
  children: ReactNode;
}

function QueryProvider({ children }: QueryProviderProps) {
  const { t } = useTranslation('messages');

  const handleError = useCallback((error: Error) => {
    console.error(error);
    toast.error(t('query.errorTitle'), {
      description: t('query.errorDescription'),
      duration: 5000,
    });
  }, [t]);

  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          mutations: {
            onError: handleError,
            networkMode: 'always',
            gcTime: 0,
            retry: false,
          },
          queries: {
            networkMode: 'always',
            // 데이터가 fresh -> stale 상태로 변경되는데 걸리는 시간
            // fresh 상태일때는 쿼리 인스턴스가 새롭게 mount 되어도 네트워크 fetch가 일어나지 않는다.
            staleTime: 1000 * 60 * 10,

            // 데이터가 inactive 상태일 때 캐싱된 상태로 남아있는 시간
            // cacheTime이 지나기 전에 쿼리 인스턴스가 다시 마운트 되면, 데이터를 fetch하는 동안 캐시 데이터를 보여준다.
            gcTime: 1000 * 60 * 10,

            retry: false,
            refetchOnMount: false, // 마운트 시 자동 리패치 비활성화
            refetchOnWindowFocus: false, // 윈도우 포커스 시 자동 리패치 비활성화
            refetchOnReconnect: false, // 네트워크 재연결 시 자동 리패치 비활성화
            refetchInterval: false, // 주기적인 리패치 비활성화
          },
        },
        queryCache: new QueryCache({
          onError: (error) => {
            handleError(error);

            // 실패시 캐싱하지 않음. 주석해도 잘 동작함?
            // queryClient.removeQueries({ queryKey: query.queryKey });
          },
        }),
      }),
    [handleError]
  );

  return (
    <QueryClientProvider client={queryClient}>
      {children}
    </QueryClientProvider>
  );
}

export default QueryProvider;
